import type {CourierMessage} from '@frontembed/lib/helpers/iframe';
import {MessageCourier} from '@frontembed/lib/helpers/iframe';

import {buildPageDetailsBootToolPayload} from '#/boot/tools/pageDetailsBootTool';
import {requestScreenshotBootToolAsync} from '#/boot/tools/screenshotBootTool';
import {dispatchConversationEventCustomEvent} from '#/customEvent/conversationEvent';
import {dispatchConversationIdCustomEvent} from '#/customEvent/conversationId';
import {dispatchFeatureFlagsCustomEvent} from '#/customEvent/featureFlags';
import {dispatchPreferencesCustomEvent} from '#/customEvent/preferences';
import type {BootMessageData, FroyoMessageData} from '#/helpers/iframe/courier/types';
import {
  BootMessageDataTypesEnum,
  FroyoMessageDataTypesEnum,
  MessageCourierNamesEnum,
  decodeFroyoMessageData,
} from '#/helpers/iframe/courier/types';
import type {ConversationEvent} from '#/models/companionEvent';
import type {ViewsEnum} from '#/models/frame';
import {checkIsDevelopmentMode, checkIsProductionDeployment} from '#config/helpers';
import {getConfig} from '#config/index';

/*
 * Constants.
 */

const config = getConfig();

/*
 * Boot message courier.
 */

export class BootMessageCourier extends MessageCourier<FroyoMessageData, BootMessageData> {
  constructor(
    currentWindow: Window,
    private readonly iframe: HTMLIFrameElement,
    targetWindow: Window,
    targetWindowOrigin: string,
    onReady: () => void,
    private readonly onStyle: (cssVariables: Record<string, unknown>) => void,
    private readonly applyIframeViewStyles: (view: ViewsEnum) => void,
    private readonly onConversationChange?: (conversationId: string | undefined) => void,
    private readonly onConversationEvent?: (event: ConversationEvent) => void,
    private readonly onContentSize?: (size: {width: number; height: number} | undefined) => void,
  ) {
    super(
      MessageCourierNamesEnum.BOOT,
      MessageCourierNamesEnum.FROYO,
      currentWindow,
      targetWindow,
      targetWindowOrigin,
      decodeFroyoMessageData,
      () => {
        this.postMessage({type: BootMessageDataTypesEnum.BOOT_VERSION, version: config.version});
        onReady();
      },
      checkIsDevelopmentMode(config.mode),
    );
  }

  handleMessage = (message: CourierMessage<FroyoMessageData>): void => {
    switch (message.data.type) {
      case FroyoMessageDataTypesEnum.FROYO_VERSION: {
        // eslint-disable-next-line no-console
        console.log('[Boot] froyo version received:', message.data.version);
        break;
      }
      case FroyoMessageDataTypesEnum.VIEW: {
        this.applyIframeViewStyles(message.data.view);
        break;
      }
      case FroyoMessageDataTypesEnum.STYLE: {
        this.onStyle(message.data.cssVariables);
        break;
      }
      case FroyoMessageDataTypesEnum.FEATURE_FLAGS: {
        // This event should not be received in production unless connected from a local dev environment.
        if (checkIsProductionDeployment(config)) {
          console.warn('[Boot] froyo feature flags not handled');
          return;
        }
        dispatchFeatureFlagsCustomEvent(message.data.flags);
        break;
      }
      case FroyoMessageDataTypesEnum.CONVERSATION_ID: {
        this.onConversationChange?.(message.data.conversationId);
        dispatchConversationIdCustomEvent(message.data.conversationId);
        break;
      }
      case FroyoMessageDataTypesEnum.CONVERSATION_EVENT: {
        this.onConversationEvent?.(message.data.event);
        dispatchConversationEventCustomEvent(message.data.event);
        break;
      }
      case FroyoMessageDataTypesEnum.REQUEST_SCREENSHOT: {
        void this.handleScreenshotRequest(message);
        break;
      }
      case FroyoMessageDataTypesEnum.REQUEST_PAGE_DETAILS: {
        this.postMessage(buildPageDetailsBootToolPayload());
        break;
      }
      case FroyoMessageDataTypesEnum.OPEN_URL: {
        // eslint-disable-next-line lingui-ts/no-unlocalized-strings
        window.open(message.data.url, '_blank', 'noopener,noreferrer');
        break;
      }
      case FroyoMessageDataTypesEnum.PREFERENCES: {
        // This event should not be received in production unless connected from a local dev environment.
        if (checkIsProductionDeployment(config)) {
          console.warn('[Boot] froyo preferences not handled');
          return;
        }
        dispatchPreferencesCustomEvent(message.data.preferences);
        break;
      }
      case FroyoMessageDataTypesEnum.CONTENT_SIZE: {
        this.onContentSize?.({width: message.data.width, height: message.data.height});
        break;
      }
      case FroyoMessageDataTypesEnum.RESET_CONTENT_SIZE: {
        this.onContentSize?.(undefined);
        break;
      }
      default:
    }
  };

  private async handleScreenshotRequest(message: CourierMessage<FroyoMessageData>): Promise<void> {
    if (message.data.type !== FroyoMessageDataTypesEnum.REQUEST_SCREENSHOT) {
      throw new Error('Invalid message data type for screenshot request');
    }
    try {
      const screenshotDataUrl = await requestScreenshotBootToolAsync({
        shouldCrop: message.data.shouldCrop,
        cropText: message.data.cropText,
        iframe: this.iframe,
      });
      this.postMessage({type: BootMessageDataTypesEnum.SCREENSHOT, screenshotDataUrl});
    } catch (error) {
      console.error('FrontCompanion.screenshot error:', error);
      this.postMessage({type: BootMessageDataTypesEnum.SCREENSHOT});
    }
  }
}
